// components/ExpirationDateTooltip.tsx
'use client';
import { useEffect, useRef } from 'react';

interface ExpirationDateTooltipProps {
    isOpen: boolean;
    onClose: () => void;
    expirationDate: string | null;
    buttonRef: React.RefObject<HTMLButtonElement | null>;
}

export default function ExpirationDateTooltip({
    isOpen,
    onClose,
    expirationDate,
    buttonRef
}: ExpirationDateTooltipProps) {
    const tooltipRef = useRef<HTMLDivElement>(null);

    // Закрытие при нажатии Escape или клике вне тултипа
    useEffect(() => {
        const handleEscape = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                onClose();
            }
        };

        const handleClickOutside = (e: MouseEvent) => {
            if (
                tooltipRef.current &&
                !tooltipRef.current.contains(e.target as Node) &&
                buttonRef.current &&
                !buttonRef.current.contains(e.target as Node)
            ) {
                onClose();
            }
        };

        if (isOpen) {
            document.addEventListener('keydown', handleEscape);
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('keydown', handleEscape);
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isOpen, onClose, buttonRef]);

    if (!isOpen || !expirationDate) return null;

    return (
        <div
            ref={tooltipRef}
            className="tooltip-container"
            style={{
                position: 'absolute',
                bottom: '55px', // Позиционируем над кнопкой
                left: '10px',
                zIndex: 50,
                opacity: 0,
                transform: 'translateY(10px) scale(0.9)',
                animation: 'tooltipSlideIn 0.2s ease-out forwards',
            }}
        >
            {/* Основной контент тултипа */}
            <div
                className="tooltip-content"
                style={{
                    background: '#ffffff',
                    color: '#333333',
                    padding: '12px 16px',
                    borderRadius: '12px',
                    border: '1px solid #d1d5db',
                    fontSize: '14px',
                    fontWeight: '500',
                    whiteSpace: 'nowrap',
                    position: 'relative',
                }}
            >
                <div style={{
                    fontSize: '12px',
                    color: '#6b7280',
                    marginBottom: '4px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                }}>
                    Годен до
                </div>
                <div style={{
                    fontSize: '14px',
                    fontWeight: '500',
                    color: '#111827'
                }}>
                    {expirationDate}
                </div>

                {/* Стрелочка */}
                <div
                    style={{
                        position: 'absolute',
                        bottom: '-6px',
                        left: '20px',
                        width: '12px',
                        height: '12px',
                        background: '#ffffff',
                        transform: 'rotate(45deg)',
                        border: '1px solid #d1d5db',
                        borderTop: 'none',
                        borderLeft: 'none',
                    }}
                />
            </div>

            <style jsx>{`
                @keyframes tooltipSlideIn {
                    from {
                        opacity: 0;
                        transform: translateY(10px) scale(0.9);
                    }
                    to {
                        opacity: 1;
                        transform: translateY(0) scale(1);
                    }
                }
                
                .tooltip-container::before {
                    content: '';
                    position: 'absolute';
                    bottom: -7px;
                    left: 19px;
                    width: 14px;
                    height: 14px;
                    background: transparent;
                    z-index: -1;
                }
            `}</style>
        </div>
    );
}